import type { LoginResult } from '@bco/types';
import {
  authApi,
  BascAlert,
  BascBox,
  BascButton,
  BascCard,
  BascCenter,
  BascFormErrorSummary,
  BascGroup,
  BascLanguageSelector,
  BascPasswordInput,
  BascPinInput,
  BascStack,
  BascText,
  BascTextInput,
  useAuth,
  useAuthStore,
  useTranslation,
} from '@bco/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BrandLogo } from '../../components/BrandLogo';
import { loadClientAssets } from '../../federationInit';
import { buildLoginBackgroundStyle } from '../../utils/loginBackgroundStyle';
import { classifyChallengeError, formatCountdown } from './loginMfa';
import './login.css';

interface ClientAssets {
  appLogo?: string;
  appLogoDark?: string;
  loginBackground?: string;
}

const DEV_STORAGE_KEY = 'bco-dev-login';
const isDev = import.meta.env.DEV;

/**
 * Dev-only: read the saved login credentials from localStorage. A corrupted
 * `bco-dev-login` value must never white-screen the login page, so parse
 * failures fall back to an empty object.
 */
function readSavedDevCreds(): { email?: string; password?: string } {
  if (!isDev) {
    return {};
  }
  try {
    const raw = localStorage.getItem(DEV_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/**
 * sessionStorage flag set by the backup-code login success path so the shell
 * shows the recovery banner once the user lands in the app.
 */
const RECOVERY_LOGIN_FLAG = 'bco.recoveryLogin';
/**
 * sessionStorage flag set when a recovery (no-refresh-token) session is
 * detected, so the login screen can show a one-time "session ended" notice.
 */
const RECOVERY_SESSION_ENDED_FLAG = 'bco.recoverySessionEnded';

type Step = 'credentials' | 'mfa-challenge';

export function LoginPage() {
  const { clientName = '' } = useParams<{ clientName: string }>();
  const navigate = useNavigate();
  const { login, isLoading, error, isAuthenticated, clearError } = useAuth();
  const { t } = useTranslation();

  // Dev only: restore last used credentials (parse failures fall back to {}).
  const parsed = readSavedDevCreds();
  const [email, setEmail] = useState(parsed.email || '');
  const [password, setPassword] = useState(parsed.password || '');
  const [assets, setAssets] = useState<ClientAssets | null>(null);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);

  // --- MFA challenge step state (component-only; never persisted) ---
  const [step, setStep] = useState<Step>('credentials');
  const [session, setSession] = useState<string | null>(null);
  const [challengeExpiresAt, setChallengeExpiresAt] = useState<number | null>(
    null
  );
  const [code, setCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [mfaError, setMfaError] = useState<{
    color: 'red' | 'yellow';
    message: string;
  } | null>(null);
  const [mfaSubmitting, setMfaSubmitting] = useState(false);
  const [inputLocked, setInputLocked] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  // One-time "recovery session ended" informational notice.
  const [showSessionEnded, setShowSessionEnded] = useState(false);

  const pinRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const challengeHeadingRef = useRef<HTMLHeadingElement>(null);
  const mfaErrorId = 'login-mfa-error';

  useEffect(() => {
    if (sessionStorage.getItem(RECOVERY_SESSION_ENDED_FLAG)) {
      setShowSessionEnded(true);
      sessionStorage.removeItem(RECOVERY_SESSION_ENDED_FLAG);
    }
  }, []);

  useEffect(() => {
    async function loadAssets() {
      const clientAssets = await loadClientAssets(clientName);
      setAssets(clientAssets);
    }
    if (clientName) {
      loadAssets();
    }
  }, [clientName]);

  useEffect(() => {
    async function checkRegistration() {
      try {
        const gatewayUrl = import.meta.env.VITE_GATEWAY_URL || '';
        const res = await fetch(
          `${gatewayUrl}/api/auth/registration-enabled?clientName=${encodeURIComponent(clientName)}`
        );
        if (res.ok) {
          const data = await res.json();
          setRegistrationEnabled(data.registrationEnabled);
        }
      } catch {
        // Default to showing the link if the check fails
      }
    }
    if (clientName) {
      checkRegistration();
    }
  }, [clientName]);

  const validatingRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || validatingRef.current) {
      return;
    }
    validatingRef.current = true;

    authApi.validateToken().then((valid) => {
      if (valid) {
        navigate(`/${clientName}`, { replace: true });
      } else {
        useAuthStore.getState().clearAuthStorage();
      }
      validatingRef.current = false;
    });
  }, [isAuthenticated, clientName, navigate]);

  // Clear error on unmount or client change
  useEffect(() => {
    return () => clearError();
  }, [clientName, clearError]);

  // Move focus to the challenge heading when entering the MFA step.
  useEffect(() => {
    if (step === 'mfa-challenge') {
      challengeHeadingRef.current?.focus();
    }
  }, [step]);

  // Return to the credentials step, clearing all challenge state. Optionally
  // focuses the password field (used by the session-expired path).
  const returnToCredentials = useCallback((focusPassword: boolean) => {
    setStep('credentials');
    setSession(null);
    setChallengeExpiresAt(null);
    setCode('');
    setBackupCode('');
    setUseBackup(false);
    setMfaSubmitting(false);
    setInputLocked(false);
    if (focusPassword) {
      // Defer until the credentials form is mounted.
      window.requestAnimationFrame(() => passwordRef.current?.focus());
    }
  }, []);

  const handleSessionExpired = useCallback(() => {
    setMfaError({
      color: 'yellow',
      message: t('auth.mfa.error.sessionExpired'),
    });
    returnToCredentials(true);
  }, [returnToCredentials, t]);

  // Countdown to challengeExpiresAt; proactively trips the session-expired path
  // when it reaches zero.
  useEffect(() => {
    if (step !== 'mfa-challenge' || challengeExpiresAt == null) {
      return;
    }
    const tick = () => {
      const remaining = Math.round(challengeExpiresAt - Date.now() / 1000);
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        handleSessionExpired();
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [step, challengeExpiresAt, handleSessionExpired]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      return;
    }

    // Dev only: save credentials for next restart
    if (isDev) {
      localStorage.setItem(
        DEV_STORAGE_KEY,
        JSON.stringify({ email, password })
      );
    }

    try {
      const result: LoginResult = await login(clientName, email, password);

      if (result.status === 'MFA_REQUIRED') {
        setSession(result.session);
        setChallengeExpiresAt(result.challengeExpiresAt);
        setMfaError(null);
        setUseBackup(false);
        setCode('');
        setBackupCode('');
        setInputLocked(false);
        setStep('mfa-challenge');
      } else if (result.status === 'MFA_ENROLLMENT_REQUIRED') {
        // Hand the enrollment token to the mandatory-setup interstitial via
        // in-memory router state — NEVER localStorage.
        navigate(`/${clientName}/setup-mfa`, {
          state: {
            enrollmentToken: result.enrollmentToken,
            challengeExpiresAt: result.challengeExpiresAt,
          },
        });
      }
      // SUCCESS: the store authenticated; the validateToken effect redirects.
    } catch {
      // Credential error is handled by the auth store (shown via `error`).
    }
  };

  const submitChallenge = useCallback(
    async (value: string) => {
      if (!session || mfaSubmitting || inputLocked) {
        return;
      }
      setMfaSubmitting(true);
      setMfaError(null);
      try {
        const response = await authApi.respondChallenge({
          session,
          type: useBackup ? 'backup_code' : 'totp',
          value,
        });

        // Authenticate the store like a normal login. Tokens are already in
        // localStorage (authApi.respondChallenge persisted them).
        useAuthStore.setState({ accessToken: response.accessToken });
        useAuthStore.getState().setUser(response.user);

        if (useBackup) {
          // Flag the recovery banner BEFORE redirect.
          sessionStorage.setItem(RECOVERY_LOGIN_FLAG, '1');
        }

        navigate(`/${clientName}`, { replace: true });
      } catch (err) {
        const outcome = classifyChallengeError(err, useBackup);
        switch (outcome.kind) {
          case 'invalid':
            setMfaError({ color: 'red', message: t(outcome.messageKey) });
            setCode('');
            setBackupCode('');
            window.requestAnimationFrame(() => pinRef.current?.focus());
            break;
          case 'session-expired':
            handleSessionExpired();
            break;
          case 'too-many':
            setMfaError({ color: 'red', message: t(outcome.messageKey) });
            setInputLocked(true);
            break;
          case 'network':
            setMfaError({ color: 'red', message: t(outcome.messageKey) });
            break;
        }
      } finally {
        setMfaSubmitting(false);
      }
    },
    [
      session,
      mfaSubmitting,
      inputLocked,
      useBackup,
      clientName,
      navigate,
      handleSessionExpired,
      t,
    ]
  );

  const handleChallengeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = useBackup ? backupCode.trim() : code;
    if (!value || (!useBackup && value.length < 6)) {
      return;
    }
    void submitChallenge(value);
  };

  const toggleBackup = () => {
    setUseBackup((prev) => !prev);
    setMfaError(null);
    setCode('');
    setBackupCode('');
  };

  const backgroundStyle = buildLoginBackgroundStyle(assets);

  return (
    <BascBox className="login-page" style={backgroundStyle}>
      <BascBox className="login-overlay" />
      <BascCenter className="login-container">
        <BascCard className="login-card" shadow="xl" padding="xl" radius="md">
          {/* Logo (shared across steps) */}
          {assets?.appLogo && (
            <BascGroup justify="center" mb="lg">
              <BrandLogo
                src={assets.appLogo}
                srcDark={assets.appLogoDark}
                alt="BCO PORTAL"
                className="login-logo"
              />
            </BascGroup>
          )}

          {step === 'credentials' && (
            <form onSubmit={handleSubmit}>
              <BascStack gap="lg">
                <BascText
                  className="login-title"
                  ta="center"
                  textRole="heading1"
                  fw={600}
                >
                  {t('auth.signIn')}
                </BascText>

                {/* One-time recovery session-ended notice */}
                {showSessionEnded && (
                  <BascAlert
                    color="blue"
                    variant="light"
                    title={t('auth.mfa.recovery.sessionEndedTitle')}
                    withCloseButton
                    onClose={() => setShowSessionEnded(false)}
                  >
                    {t('auth.mfa.recovery.sessionEndedBody')}
                  </BascAlert>
                )}

                {/* Credential / server errors are non-field: they render in the
                    summary (role=alert, takes focus on failure), never pinned on
                    the email or password input. */}
                <BascFormErrorSummary
                  errors={error ? [{ message: error }] : []}
                />

                <BascTextInput
                  label={t('auth.email')}
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  required
                  type="email"
                  autoComplete="email"
                  disabled={isLoading}
                />

                <BascPasswordInput
                  ref={passwordRef}
                  label={t('auth.password')}
                  placeholder=""
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  required
                  autoComplete="current-password"
                  disabled={isLoading}
                />

                <BascText ta="right" textRole="body">
                  <a
                    href={`/${clientName}/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ''}`}
                    className="login-link"
                  >
                    {t('auth.forgotPassword')}
                  </a>
                </BascText>

                <BascButton
                  emphasis="primary"
                  type="submit"
                  fullWidth
                  loading={isLoading}
                  disabled={!email || !password}
                  className="login-submit"
                >
                  {t('auth.signIn')}
                </BascButton>

                {registrationEnabled && (
                  <BascText
                    className="login-register-link"
                    ta="center"
                    textRole="body"
                  >
                    {t('auth.dontHaveAccount')}{' '}
                    <a href={`/${clientName}/register`} className="login-link">
                      {t('auth.register')}
                    </a>
                  </BascText>
                )}

                <BascGroup justify="center">
                  <BascLanguageSelector compact label={t('language.change')} />
                </BascGroup>
              </BascStack>
            </form>
          )}

          {step === 'mfa-challenge' && (
            <form onSubmit={handleChallengeSubmit}>
              <BascStack gap="lg">
                <BascStack gap={4}>
                  <h1
                    ref={challengeHeadingRef}
                    tabIndex={-1}
                    className="login-title login-mfa-heading"
                  >
                    {t('auth.mfa.challengeTitle')}
                  </h1>
                  <BascText ta="center" textRole="body" c="dimmed">
                    {t('auth.mfa.challengeSubtitle')}
                  </BascText>
                </BascStack>

                {/* Error region — wired to the inputs via aria-describedby.
                    Assertive so SR users hear it immediately; never colour-only
                    (the message text always accompanies the colour). */}
                <div id={mfaErrorId} role="alert" aria-live="assertive">
                  {mfaError && (
                    <BascAlert color={mfaError.color} variant="light">
                      {mfaError.message}
                    </BascAlert>
                  )}
                </div>

                {!useBackup ? (
                  <BascGroup justify="center">
                    <BascPinInput
                      ref={pinRef}
                      value={code}
                      onChange={setCode}
                      onComplete={(value) => void submitChallenge(value)}
                      autoFocus
                      disabled={mfaSubmitting || inputLocked}
                      error={mfaError?.color === 'red'}
                      aria-label={t('auth.mfa.codeLabel')}
                      aria-describedby={mfaErrorId}
                      name="totp-code"
                    />
                  </BascGroup>
                ) : (
                  <BascTextInput
                    label={t('auth.mfa.backupCodeLabel')}
                    placeholder={t('auth.mfa.backupCodePlaceholder')}
                    value={backupCode}
                    onChange={(e) => setBackupCode(e.currentTarget.value)}
                    dir="ltr"
                    autoFocus
                    disabled={mfaSubmitting || inputLocked}
                    error={mfaError?.color === 'red'}
                    aria-describedby={mfaErrorId}
                    autoComplete="one-time-code"
                    style={{
                      fontFamily: 'var(--mantine-font-family-monospace)',
                    }}
                  />
                )}

                {/* Countdown — polite live region, m:ss. */}
                {challengeExpiresAt != null && (
                  <BascText
                    ta="center"
                    textRole="body"
                    c="dimmed"
                    aria-live="polite"
                  >
                    {t('auth.mfa.expiresIn', {
                      time: formatCountdown(secondsLeft),
                    })}
                  </BascText>
                )}

                <BascButton
                  emphasis="primary"
                  type="submit"
                  fullWidth
                  loading={mfaSubmitting}
                  disabled={
                    inputLocked ||
                    (useBackup ? !backupCode.trim() : code.length < 6)
                  }
                >
                  {t('auth.mfa.verify')}
                </BascButton>

                <BascButton
                  type="button"
                  variant="subtle"
                  fullWidth
                  onClick={toggleBackup}
                  disabled={inputLocked}
                >
                  {useBackup
                    ? t('auth.mfa.useAuthenticatorCode')
                    : t('auth.mfa.useBackupCode')}
                </BascButton>

                <BascButton
                  type="button"
                  variant="subtle"
                  fullWidth
                  onClick={() => returnToCredentials(false)}
                >
                  {t('auth.mfa.backToSignIn')}
                </BascButton>

                <BascGroup justify="center">
                  <BascLanguageSelector compact label={t('language.change')} />
                </BascGroup>
              </BascStack>
            </form>
          )}
        </BascCard>
      </BascCenter>
    </BascBox>
  );
}

export default LoginPage;
